import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { RohsComponentComplianceFileDetailView } from '@/shared/hooks/api/rohsComplianceFiles'
import {
  useCompleteCatalogRohsComponentComplianceFile,
  useUncompleteCatalogRohsComponentComplianceFile,
} from '@/shared/hooks/api/rohsComplianceFiles'
import { useComplianceActorDisplayLabel } from '@/shared/hooks/useComplianceActorDisplayLabel'
import { Button } from '@/components/ui/button'

function formatCompletedAt(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso))
}

export function V2ComponentRohsSignOffActions({
  componentId,
  file,
}: {
  componentId: string
  file: RohsComponentComplianceFileDetailView
}) {
  const { t, i18n } = useTranslation('common')
  const { mutateAsync: completeFile, isPending: isCompleting } =
    useCompleteCatalogRohsComponentComplianceFile(componentId)
  const { mutateAsync: uncompleteFile, isPending: isUncompleting } =
    useUncompleteCatalogRohsComponentComplianceFile(componentId)

  const isPending = isCompleting || isUncompleting
  const isComplete = file.completedAt != null
  const actorLabel = useComplianceActorDisplayLabel(file.completedByActor)

  const handleComplete = async () => {
    try {
      const result = await completeFile()
      if (result.warnings.includes('COMPONENT_EVIDENCE_INCOMPLETE')) {
        toast.warning(
          t(
            'v2.pages.products.detail.rohsFiles.componentFile.warnings.componentEvidenceIncomplete'
          )
        )
      }
    } catch {
      toast.error(
        t(
          'v2.pages.products.detail.rohsFiles.componentFile.actions.completeFailed'
        )
      )
    }
  }

  const handleUncomplete = async () => {
    try {
      await uncompleteFile()
    } catch {
      toast.error(
        t(
          'v2.pages.products.detail.rohsFiles.componentFile.actions.uncompleteFailed'
        )
      )
    }
  }

  return (
    <div className='flex flex-wrap items-center justify-end gap-2'>
      {isComplete && file.completedAt != null && file.completedByActor != null ? (
        <span className='text-xs text-muted-foreground'>
          {t('v2.pages.products.detail.rohsFiles.completedAt', {
            date: formatCompletedAt(file.completedAt, i18n.language),
            actor: actorLabel,
          })}
        </span>
      ) : null}
      {isComplete ? (
        <Button
          type='button'
          variant='outline'
          size='sm'
          disabled={isPending}
          onClick={() => {
            void handleUncomplete()
          }}
        >
          {t(
            'v2.pages.products.detail.rohsFiles.componentFile.actions.uncomplete'
          )}
        </Button>
      ) : (
        <Button
          type='button'
          size='sm'
          disabled={isPending}
          onClick={() => {
            void handleComplete()
          }}
        >
          {t(
            'v2.pages.products.detail.rohsFiles.componentFile.actions.complete'
          )}
        </Button>
      )}
    </div>
  )
}
